/**
 * Sector Operations — Stays Owner Digest & Outside Link Client Service.
 * R39 K9.a (plan docs/plans/sector-operations.md SO-P121, SO-D300, SO-D202, SO-D207, SO-P68, SO-P70).
 */

import { callable } from './callable'
import { getApprovalsFunctions } from './approvalsApp'
import type {
  SetOwnerFeedInput,
  RemoveFeedOwnerInput,
  ReissueOwnerFeedLinkInput,
  GetOwnerFeedInput,
  ListOwnerDigestsInput,
  SendOwnerDigestNowInput,
  GetOwnerFeedLinkInfoInput,
  PrepareOwnerFeedOtpInput,
  OpenOwnerFeedInput,
  OpsOwnerFeedDoc,
  OpsOwnerDigestDoc,
  OpsOwnerDigestSnapshot,
} from '../types/ops'

// ---------------------------------------------------------------------------
// 1. Workspace-scoped callables (Owner / Admin, Editors with ops access)
// ---------------------------------------------------------------------------

export async function setOwnerFeed(
  input: SetOwnerFeedInput,
): Promise<{
  success: boolean
  feed: OpsOwnerFeedDoc
  links: Array<{ ownerId: string; linkVersion: number; token: string }>
}> {
  return callable('setOwnerFeed', input)
}

export async function removeFeedOwner(
  input: RemoveFeedOwnerInput,
): Promise<{ success: boolean; version: number }> {
  return callable('removeFeedOwner', input)
}

export async function reissueOwnerFeedLink(
  input: ReissueOwnerFeedLinkInput,
): Promise<{ success: boolean; ownerId: string; linkVersion: number; token: string }> {
  return callable('reissueOwnerFeedLink', input)
}

export async function getOwnerFeed(
  input: GetOwnerFeedInput,
): Promise<{ feed: OpsOwnerFeedDoc | null }> {
  return callable('getOwnerFeed', input)
}

export interface OwnerDigestListItem {
  id: string
  dateKey: string
  builtAt: number
  sentTo: OpsOwnerDigestDoc['sentTo']
  readyCount: number
  openIssuesCount: number
}

export async function listOwnerDigests(
  input: ListOwnerDigestsInput,
): Promise<{ digests: OwnerDigestListItem[] }> {
  return callable('listOwnerDigests', input)
}

export async function sendOwnerDigestNow(
  input: SendOwnerDigestNowInput,
): Promise<{ success: boolean; dateKey: string; sent: unknown }> {
  return callable('sendOwnerDigestNow', input)
}

// ---------------------------------------------------------------------------
// 2. Outside link callables (Approvals App instance)
// ---------------------------------------------------------------------------

export async function getOwnerFeedLinkInfo(
  input: GetOwnerFeedLinkInfoInput,
): Promise<{
  propertyName: string
  ownerName: string
  phoneMasked: string
  linkVersion?: number
}> {
  return callable('getOwnerFeedLinkInfo', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function prepareOwnerFeedOtp(
  input: PrepareOwnerFeedOtpInput,
): Promise<{ success: boolean; attemptsRemaining: number }> {
  return callable('prepareOwnerFeedOtp', input, {
    functions: getApprovalsFunctions(),
  })
}

export async function openOwnerFeed(
  input: OpenOwnerFeedInput,
): Promise<{
  propertyName: string
  ownerName: string
  digests: Array<{
    dateKey: string
    builtAt: number
    snapshot: OpsOwnerDigestSnapshot
  }>
}> {
  return callable('openOwnerFeed', input, {
    functions: getApprovalsFunctions(),
  })
}
